import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpHeaders,
} from '@angular/common/http';
import type { Tokens } from '@okta/okta-auth-js';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import environment from '@environments/environment';
import { AuthService } from '@auth/auth.service';
import { getFileNameTimeStamp } from '@shared/services/utils.service';
import {
  extractContentDispositionExtension,
  saveAs,
} from '@shared/utils/download.util';
import { ZipProgressEvent } from '@shared/types';
import {
  createDeleteFileWorker,
  createZipFileWorker,
} from './service-worker.util';

interface DeleteFiles {
  id: string;
  entityId: string;
  entityType: string;
}

interface GridFile {
  id: string;
  title: string;
  url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly _http = inject(HttpClient);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _authService = inject(AuthService);

  private zipWorker?: Worker;
  private deleteWorker?: Worker;
  constructor() {
    this.zipWorker = createZipFileWorker() as Worker;
    this.deleteWorker = createDeleteFileWorker() as Worker;
  }

  downloadFile(id: string) {
    const token = this._authService.getToken();
    const httpOptions = {
      responseType: 'arraybuffer' as 'json',
      observe: 'response' as 'body',
      headers: new HttpHeaders({
        DocumentDownload: '',
        Authorization: `Bearer ${token}`,
      }),
    };

    return this._http
      .get(`${environment.irisApi}/document/${id}/`, httpOptions)
      .pipe(catchError((error) => of(error)));
  }

  /**
   * Requests a document's binary payload while reporting transfer progress.
   *
   * Identical to {@link FileService.downloadFile} in URL, headers, and response
   * type, but observes the full `HttpEvent` stream with `reportProgress` so
   * callers can render a determinate progress indicator. The existing
   * {@link FileService.downloadFile} is deliberately left untouched — every
   * current consumer expects a single `HttpResponse` emission.
   *
   * @remarks Transport errors are **not** swallowed here. Unlike
   * `downloadFile`, failures propagate on the error channel so callers can
   * classify them by status. Callers that need the old error-as-next behaviour
   * must add their own `catchError`.
   *
   * @remarks The request opts into the interceptor's `silent-mode`
   * (ERM-13269): the Outcomes Ready card owns this download's failure UX with
   * classified toasts, so the global interceptor must pass the raw
   * `HttpErrorResponse` through — its string rethrows destroy the status the
   * classifier needs — and raise no snackbar of its own.
   *
   * @remarks A determinate percentage requires a usable `Content-Length` on
   * the response. `Content-Length` is a CORS-safelisted response header, so
   * being cross-origin is **not** on its own a reason for it to be missing —
   * `Access-Control-Expose-Headers` is not required for it. In practice this
   * endpoint often yields no usable value anyway (the API copies upstream
   * document-store headers onto a re-encoded body), so
   * `HttpProgressEvent.total` may be `undefined` and consumers must handle a
   * null percentage rather than fabricate a byte count.
   *
   * @param id - Identifier of the document to download.
   * @returns Observable of the raw `HttpEvent` stream, including
   * `HttpEventType.DownloadProgress` events and the final `HttpResponse`.
   *
   * @example
   * ```typescript
   * this._fileService.downloadFileWithProgress(docId).subscribe(event => {
   *   if (event.type === HttpEventType.DownloadProgress) { ... }
   * });
   * ```
   */
  downloadFileWithProgress(id: string): Observable<HttpEvent<ArrayBuffer>> {
    const token = this._authService.getToken();

    return this._http.get(`${environment.irisApi}/document/${id}/`, {
      responseType: 'arraybuffer',
      observe: 'events',
      reportProgress: true,
      context: new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode'),
      headers: new HttpHeaders({
        DocumentDownload: '',
        Authorization: `Bearer ${token}`,
      }),
    });
  }

  async downloadFiles(
    files: GridFile[],
    fileCategory?: string,
    fileId?: string,
  ) {
    if (files.length === 1) {
      this.showSnackbar('Downloading File...', 'snackbar-loading');
      this.downloadFile(files[0].id).subscribe((response) => {
        if (response.ok) {
          this.saveFile(response, files[0].title);
          this.showSnackbar('Download complete', 'snackbar-success');
        } else {
          this.showSnackbar(
            'Failed to download file. Please try to re-download the failed file. If you continue to have issues, please contact WCG Client Care as listed under Contact Us.',
            'snackbar-error',
          );
        }
      });
    } else {
      this.downloadAsZip(files, fileCategory, fileId);
    }
  }

  /**
   * Zips several documents and announces the result with the standard
   * snackbars.
   *
   * A thin wrapper over {@link FileService.zipDocuments}: it owns the generated
   * archive name and the platform's default messaging, while the shared core
   * owns the worker plumbing. Behaviour for existing callers is unchanged.
   *
   * @param files - Documents to include in the archive.
   * @param fileCategory - Category segment of the generated archive name.
   * @param fileId - Optional identifier prefixed to the archive name.
   */
  async downloadAsZip(
    files: GridFile[],
    fileCategory?: string,
    fileId?: string,
  ) {
    this.showSnackbar('Downloading ZIP File...', 'snackbar-loading');

    const timeStamp = getFileNameTimeStamp('yyyyMMDDHHmmss').replace(
      /[\s+_]/g,
      '',
    );
    const zipFileName = `${fileId ? fileId + '_' : ''}${fileCategory}_${timeStamp}.zip`;

    const _allFailed =
      'All files failed to download. Please try to re-download the failed files. If you continue to have issues, please contact WCG Client Care as listed under Contact Us';

    try {
      // `lastValueFrom` takes the terminal `done` event and unsubscribes on
      // completion. A bare `.subscribe()` here would leave a live subscription
      // inside a `providedIn: 'root'` singleton with nothing to tear it down.
      const _result = await lastValueFrom(
        this.zipDocuments(files, zipFileName),
      );

      if (_result.type !== 'done' || !_result.savedArchive) {
        this.showSnackbar(_allFailed, 'snackbar-error');
        return;
      }

      if (_result.failedCount > 0) {
        this.showSnackbar(
          `Some files failed to download. More info available inside zip file under 'Failed Downloads.txt' Please try to re-download the failed files. If you continue to have issues, please contact WCG Client Care as listed under Contact Us.`,
          'snackbar-warning',
        );
        return;
      }

      this.showSnackbar('Download complete', 'snackbar-success');
    } catch {
      this.showSnackbar(_allFailed, 'snackbar-error');
    }
  }

  /**
   * Zips several documents in the background worker, reporting per-document
   * progress and raising **no** snackbars of its own.
   *
   * This is the single zip implementation; {@link FileService.downloadAsZip}
   * wraps it to add the platform's default messaging. Callers that need their
   * own copy — or the result at all — subscribe here instead, because
   * `downloadAsZip` returns `Promise<void>` and swallows the outcome.
   *
   * @param files - Documents to include in the archive.
   * @param zipFileName - File name for the produced archive.
   * @returns Observable emitting `progress` events as each document settles,
   * then exactly one `done` event carrying the archive and the per-document
   * failures. Completes after the `done` event; errors if no access token is
   * available (expired/cleared Okta session) or the worker fails to start.
   *
   * @example
   * ```typescript
   * this._fileService.zipDocuments(files, 'outcomes.zip').subscribe(e => {
   *   if (e.type === 'progress') { render(e.completed, e.total); }
   * });
   * ```
   */
  zipDocuments(
    files: GridFile[],
    zipFileName: string,
  ): Observable<ZipProgressEvent> {
    return new Observable<ZipProgressEvent>((observer) => {
      if (!this.zipWorker) {
        observer.error(new Error('Zip worker is unavailable'));
        return;
      }

      const _worker = this.zipWorker;

      _worker.onmessage = ({ data }) => {
        if (data?.type === 'progress') {
          observer.next({
            type: 'progress',
            completed: data.completed,
            total: data.total,
          });
          return;
        }

        // An all-failed archive contains nothing but `Failed Downloads.txt`, so
        // it is deliberately not saved — handing the user an empty zip reads as
        // a successful download.
        const _shouldSave = !!data?.zipBlob && !data?.hasAllFailed;
        if (_shouldSave) {
          this.saveAs(data.zipBlob, zipFileName);
        }

        observer.next({
          type: 'done',
          savedArchive: _shouldSave,
          hasAllFailed: !!data?.hasAllFailed,
          successCount: data?.successCount ?? 0,
          failedCount: data?.failedCount ?? 0,
          failures: data?.failures ?? [],
        });
        observer.complete();
      };

      _worker.onerror = () => observer.error(new Error('Zip worker failed'));

      this._authService
        .getToken()
        .then((tokens: Tokens) => {
          // Okta's Tokens.accessToken is optional — it is absent once the
          // session expires or another tab logs out. Fail fast here: the
          // worker fetches with plain `fetch`, outside the auth interceptor,
          // so nothing downstream can recover a missing token.
          const _accessToken = tokens?.accessToken?.accessToken;
          if (!_accessToken) {
            observer.error(
              new Error('No access token available to download documents'),
            );
            return;
          }
          // Only the JWT string crosses the worker boundary — the ID and
          // refresh tokens have no business inside the zip worker.
          _worker.postMessage({ files, token: _accessToken });
        })
        .catch((error: unknown) => observer.error(error));
    });
  }

  saveAs(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  saveFile(data: any, fileName: string) {
    const blob = new Blob([data.body], { type: 'application/octet-stream' });
    const contentDisposition = data.headers.get('content-disposition');
    this.saveAs(
      blob,
      this.checkAndAppendFileExtension(fileName, contentDisposition),
    );
  }

  public checkAndAppendFileExtension = (
    filename: string,
    contentDisposition: string,
    appendFileName?: string,
  ): string => {
    const contentDispositionExtension =
      extractContentDispositionExtension(contentDisposition);

    if (
      contentDisposition?.indexOf('.') === -1 ||
      Boolean(contentDispositionExtension) === false
    ) {
      return filename;
    }

    const filenameExtension = filename.split('.').pop();
    let newFileName =
      contentDispositionExtension === filenameExtension
        ? filename
        : `${filename}.${contentDispositionExtension}`;

    if (appendFileName) {
      newFileName = `${newFileName.substring(
        0,
        newFileName.lastIndexOf('.'),
      )}${appendFileName}_.${contentDispositionExtension}`;
    }

    return newFileName;
  };

  deleteFile(documentId: string): Observable<any> {
    return this._http.delete(`${environment.irisApi}/document/${documentId}/`);
  }

  async deleteFiles(files: DeleteFiles[]): Promise<void> {
    if (files.length === 1) {
      await this.deleteSingleFile(files[0]);
    } else {
      await this.bulkDelete(files);
    }
    return Promise.resolve();
  }

  deleteFilesAsObservable(files: DeleteFiles[]): Observable<void> {
    return new Observable<void>((observer) => {
      (async () => {
        try {
          await this.deleteFiles(files);
          observer.next();
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();
    });
  }

  private async deleteSingleFile(file: DeleteFiles): Promise<void> {
    try {
      this.showSnackbar('Deleting document...', 'snackbar-loading');

      const response = await lastValueFrom(this.deleteFile(file.id));

      if (response) {
        this.showSnackbar('Document deleted', 'snackbar-loadsuccessing');
      } else {
        this.showSnackbar('Failed to delete document.', 'snackbar-error');
      }
    } catch {
      this.showSnackbar('Failed to delete document.', 'snackbar-error');
    }
  }

  /**
   * Deletes several documents through the background worker.
   *
   * Validates the Okta access token before posting: the worker deletes with
   * plain `fetch`, outside the auth interceptor, so a missing token cannot be
   * recovered downstream and is reported as a failure immediately.
   *
   * @param files - Documents to delete.
   */
  private async bulkDelete(files: DeleteFiles[]): Promise<void> {
    if (!this.deleteWorker) {
      return;
    }

    try {
      const tokens: Tokens = await this._authService.getToken();
      const accessToken = tokens?.accessToken?.accessToken;
      if (!accessToken) {
        this.showSnackbar('Failed to delete documents.', 'snackbar-error');
        return;
      }
      this.showSnackbar('Deleting documents...', 'snackbar-loading');
      const result = await this.postToWorker(this.deleteWorker, {
        files,
        token: accessToken,
      });

      await this.handleBulkDeletionResult(result);
    } catch {
      this.showSnackbar('Failed to delete documents.', 'snackbar-error');
    }
  }

  private postToWorker(
    worker: Worker,
    payload: any,
  ): Promise<{ success: Array<unknown>; failed: Array<unknown> }> {
    return new Promise((resolve, reject) => {
      worker.onmessage = ({ data }) => resolve(data);
      worker.onerror = (error) => reject(error);
      worker.postMessage(payload);
    });
  }

  private async handleBulkDeletionResult(data: {
    success: Array<unknown>;
    failed: Array<unknown>;
  }): Promise<void> {
    if (data.failed.length > 0) {
      this.showSnackbar(
        `Deleted ${data.success.length} documents. Failed to delete ${data.failed.length}.`,
        'snackbar-warning',
      );
    } else {
      this.showSnackbar('Bulk documents deleted', 'snackbar-success');
    }
  }

  showSnackbar(message: string, messageClass: string): void {
    this._snackBar.open(message, 'Close', {
      panelClass: messageClass,
      duration: 5000,
    });
  }
}
